import React from 'react';
import {View, Image, ScrollView, TouchableOpacity, Linking} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import SkeletonBar from '@/Atoms/SkeletonBar';
import TickCheck from '@/Atoms/TickCheck';
import CheckerboardSVG from '@/Atoms/CheckerboardSVG';
import CouponCode from '@/Atoms/CouponCode';
import CCColors from '@/Utils/CCColors';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {getValidTimezone} from '@/Utils/CommonUtils';
import {computeFinalPrice, useCouponStore} from '@/Store/couponStore';
import {seriesStyles} from './styles/seriesStyles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface SeriesRegistrationModalProps {
  tournament: any;
  masterySeries: any;
  isLoading: boolean;
  categoryAPrice: number | null;
  mrp: number;
  discountPercentage: number | null;
  isPaymentProcessing: boolean;
  errorMessage: string;
  onPayNow: () => void;
  onClose: () => void;
  couponExtraParams?: Record<string, unknown>;
}

export default function SeriesRegistrationModal({
  tournament,
  masterySeries,
  isLoading,
  categoryAPrice,
  mrp,
  discountPercentage,
  isPaymentProcessing,
  errorMessage,
  onPayNow,
  onClose,
  couponExtraParams,
}: SeriesRegistrationModalProps) {
  const {isMobileOrTab} = useMediaQuery();
  const userData = useLoggedInUserStore(state => state.userData);
  const appliedCoupon = useCouponStore(s => s.appliedCoupon);
  const pendingCoupon = useCouponStore(s => s.pendingCoupon);

  const hasDiscount =
    discountPercentage !== null &&
    discountPercentage > 0 &&
    mrp !== null &&
    mrp !== categoryAPrice;

  const couponDiscountedPrice = appliedCoupon
    ? computeFinalPrice(appliedCoupon, mrp)
    : null;

  const seriesName =
    masterySeries?.name || tournament?.parentName || tournament?.name;
  const tournamentName = tournament?.name;
  const userTz = getValidTimezone(userData?.user_timezone?.selected);
  const nextMatch = tournament?.start_date
    ? dayjs(tournament.start_date).tz(userTz).format('ddd, MMM D · h:mm A')
    : null;
  const format = tournament?.time_control;
  const enrolled = tournament?.players_count;

  if (isMobileOrTab) {
    return (
      <View style={seriesStyles.mobileSheet}>
        {/* Drag handle */}
        <View style={seriesStyles.dragHandleRow}>
          <View style={seriesStyles.dragHandle} />
        </View>

        <ScrollView
          style={seriesStyles.mobileScroll}
          showsVerticalScrollIndicator={false}>
          {/* Hero */}
          <LinearGradient
            colors={['#FFFDF5', '#FAF6EB']}
            start={{x: 0, y: 0}}
            end={{x: 0, y: 1}}
            style={seriesStyles.mobileHero}>
            <View style={seriesStyles.mobileCheckerWrap}>
              <CheckerboardSVG size={100} />
            </View>
            <View style={seriesStyles.mobileHeroIcon}>
              <Image
                source={require('@/Assets/Images/Svgs/Tournaments.svg')}
                style={{width: 52, height: 52}}
                resizeMode="contain"
              />
            </View>
            <View style={seriesStyles.mobileHeroText}>
              <CCText style={seriesStyles.mobileEyebrow} color="#806234">
                TOURNAMENT PASS
              </CCText>
              {isLoading ? (
                <SkeletonBar
                  width="90%"
                  height={32}
                  style={seriesStyles.skeletonNameGap}
                />
              ) : (
                <CCText
                  fontWeight="thunder"
                  style={seriesStyles.mobileSeriesName}
                  color="#262322">
                  {seriesName}
                </CCText>
              )}
              {!isLoading && !!tournamentName && (
                <CCText style={seriesStyles.mobileSub} color="#888280">
                  {tournamentName}
                </CCText>
              )}
            </View>
          </LinearGradient>

          {/* Meta tiles */}
          <View style={seriesStyles.metaGrid}>
            {!!nextMatch && (
              <View style={{...seriesStyles.metaTile, flex: 2}}>
                <MaterialCommunityIcons
                  name="calendar-outline"
                  size={16}
                  color="#806234"
                />
                <View style={seriesStyles.metaTileText}>
                  <CCText style={seriesStyles.metaTileLabel}>NEXT MATCH</CCText>
                  <CCText style={seriesStyles.metaTileValue} color="#262322">
                    {nextMatch}
                  </CCText>
                </View>
              </View>
            )}
            {!!format && (
              <View style={seriesStyles.metaTile}>
                <MaterialCommunityIcons
                  name="clock-outline"
                  size={16}
                  color="#806234"
                />
                <View style={seriesStyles.metaTileText}>
                  <CCText style={seriesStyles.metaTileLabel}>FORMAT</CCText>
                  <CCText style={seriesStyles.metaTileValue} color="#262322">
                    {format}
                  </CCText>
                </View>
              </View>
            )}
          </View>

          {/* Includes */}
          <View style={seriesStyles.mobileIncludes}>
            {[
              '1 tournament with opening positions',
              'Personalised review report',
              'Free Play & Learn platform',
            ].map((text, i) => (
              <View key={i} style={seriesStyles.includeRow}>
                <TickCheck />
                <CCText
                  fontSize={13}
                  fontWeight="medium"
                  color="#262322"
                  style={seriesStyles.includeText}>
                  {text}
                </CCText>
              </View>
            ))}
          </View>

          {/* Roster */}
          <View style={seriesStyles.mobileRoster}>
            <MaterialCommunityIcons
              name="account-group-outline"
              size={16}
              color="#888280"
            />
            <CCText
              fontSize={12}
              color="#888280"
              style={seriesStyles.mobileRosterText}>
              {enrolled
                ? `${enrolled} players registered`
                : 'Open registration'}
            </CCText>
          </View>
        </ScrollView>

        {/* Mobile coupon */}
        <CouponCode
          basePrice={mrp}
          currentEffectivePrice={
            appliedCoupon
              ? couponDiscountedPrice ?? undefined
              : hasDiscount
              ? categoryAPrice ?? undefined
              : undefined
          }
          discountedPrice={couponDiscountedPrice}
          currentDiscountLabel={
            !appliedCoupon && hasDiscount ? 'Series Discount' : undefined
          }
          pendingDiscountedPrice={
            pendingCoupon ? computeFinalPrice(pendingCoupon.coupon, mrp) : null
          }
          couponExtraParams={couponExtraParams}
        />

        {/* Pay bar */}
        <View style={seriesStyles.mobilePayBar}>
          <View>
            {isLoading ? (
              <SkeletonBar
                width={80}
                height={32}
                style={seriesStyles.skeletonPriceGap}
              />
            ) : (
              <>
                {(hasDiscount || couponDiscountedPrice !== null) && (
                  <CCText style={seriesStyles.mobilePayStrike} color="#888280">
                    ₹{mrp}
                  </CCText>
                )}
                <View style={seriesStyles.mobilePayAmountRow}>
                  <CCText style={seriesStyles.mobileRupee} color="#262322">
                    ₹
                  </CCText>
                  <CCText
                    fontWeight="thunder"
                    style={seriesStyles.mobilePayAmount}
                    color="#262322">
                    {String(couponDiscountedPrice ?? categoryAPrice ?? '—')}
                  </CCText>
                </View>
              </>
            )}
            <CCText style={seriesStyles.mobilePayMeta} color="#888280">
              per tournament
            </CCText>
          </View>
          <Box alignItems="center">
            <TouchableOpacity
              style={[
                seriesStyles.mobileCta,
                (isPaymentProcessing || !categoryAPrice || isLoading) &&
                  seriesStyles.ctaBtnDisabled,
              ]}
              onPress={onPayNow}
              disabled={isPaymentProcessing || !categoryAPrice || isLoading}
              activeOpacity={0.85}>
              <CCText style={seriesStyles.mobileCtaText}>
                {isPaymentProcessing
                  ? 'Processing...'
                  : `Pay ₹${
                      couponDiscountedPrice ?? categoryAPrice ?? ''
                    } & Register`}
              </CCText>
            </TouchableOpacity>
            <View style={seriesStyles.mobileTrustRow}>
              <MaterialCommunityIcons
                name="lock-outline"
                size={12}
                color="#888280"
              />
              <CCText style={seriesStyles.mobileTrustText}>
                Secure UPI &amp; Cards
              </CCText>
            </View>
          </Box>
        </View>

        {!!errorMessage && (
          <CCText
            fontSize={12}
            color={CCColors.Red}
            style={seriesStyles.mobileErrorMsg}>
            {errorMessage}
          </CCText>
        )}
      </View>
    );
  }

  // Desktop layout
  return (
    <View
      style={
        {
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(250,207,71,0.06), transparent 40%), radial-gradient(circle at 80% 70%, rgba(61,171,158,0.05), transparent 40%)',
        } as any
      }>
      {/* Header */}
      <View style={seriesStyles.modalHeader}>
        <CCText fontSize={20} fontWeight="semibold" color="#262322">
          Series Registration
        </CCText>
        <TouchableOpacity style={seriesStyles.modalCloseBtn} onPress={onClose}>
          <MaterialCommunityIcons name="close" size={18} color="#4D3F37" />
        </TouchableOpacity>
      </View>

      {/* Ticket */}
      <View style={seriesStyles.ticket}>
        {/* Left body */}
        <View style={seriesStyles.ticketBody}>
          {/* Brand */}
          <View style={seriesStyles.brand}>
            <View style={seriesStyles.desktopCheckerWrap}>
              <CheckerboardSVG size={120} />
            </View>
            <CCText style={seriesStyles.eyebrow} color="#806234">
              ✦ Tournament Pass
            </CCText>
            {isLoading ? (
              <>
                <SkeletonBar
                  width="85%"
                  height={48}
                  style={seriesStyles.skeletonNameGap}
                />
                <SkeletonBar width="60%" height={16} />
              </>
            ) : (
              <>
                <CCText
                  fontWeight="thunder"
                  style={seriesStyles.seriesName}
                  color="#262322">
                  {seriesName}
                </CCText>
                <CCText
                  fontSize={13}
                  color="#6B584D"
                  style={seriesStyles.tagline}>
                  {tournamentName}
                </CCText>
              </>
            )}
          </View>

          {/* Details */}
          <View style={seriesStyles.details}>
            {!!nextMatch && (
              <View style={seriesStyles.detailRow}>
                <MaterialCommunityIcons
                  name="calendar-outline"
                  size={18}
                  color="#806234"
                />
                <View style={seriesStyles.detailTexts}>
                  <CCText style={seriesStyles.detailLabel}>Next match</CCText>
                  <CCText fontWeight="semibold" fontSize={14} color="#262322">
                    {nextMatch}
                  </CCText>
                </View>
              </View>
            )}
            {!!format && (
              <View style={seriesStyles.detailRow}>
                <MaterialCommunityIcons
                  name="clock-outline"
                  size={18}
                  color="#806234"
                />
                <View style={seriesStyles.detailTexts}>
                  <CCText style={seriesStyles.detailLabel}>Format</CCText>
                  <CCText fontWeight="semibold" fontSize={14} color="#262322">
                    {format}
                  </CCText>
                </View>
              </View>
            )}
            <View style={seriesStyles.detailRow}>
              <MaterialCommunityIcons
                name="account-group-outline"
                size={18}
                color="#806234"
              />
              <View style={seriesStyles.detailTexts}>
                <CCText style={seriesStyles.detailLabel}>Field</CCText>
                {isLoading ? (
                  <SkeletonBar width={120} height={14} />
                ) : (
                  <CCText fontWeight="semibold" fontSize={14} color="#262322">
                    {enrolled
                      ? `${enrolled} players enrolled`
                      : 'Open registration'}
                  </CCText>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Perforation */}
        <View style={seriesStyles.perf}>
          <View style={seriesStyles.notchTop} />
          <View style={seriesStyles.perfLine} />
          <View style={seriesStyles.notchBot} />
        </View>

        {/* Stub */}
        <View style={seriesStyles.stub}>
          {/* Price */}
          <View>
            <CCText style={seriesStyles.stubLabel}>Entry fee</CCText>
            {isLoading ? (
              <SkeletonBar
                width="70%"
                height={58}
                style={seriesStyles.skeletonPriceGap}
              />
            ) : (
              <Box flexDirection="row" alignItems="flex-end" gap={10}>
                <View style={seriesStyles.priceRow}>
                  <CCText
                    fontWeight="thunder"
                    style={seriesStyles.rupee}
                    color="#262322">
                    ₹
                  </CCText>
                  <CCText
                    fontWeight="thunder"
                    style={seriesStyles.amount}
                    color="#262322">
                    {String(couponDiscountedPrice ?? categoryAPrice ?? '—')}
                  </CCText>
                </View>
                {(hasDiscount || couponDiscountedPrice !== null) && (
                  <CCText style={seriesStyles.strikePrice} color="#888280">
                    ₹{mrp}
                  </CCText>
                )}
              </Box>
            )}
            <CCText style={seriesStyles.stubMeta} color="#888280">
              per tournament · one-time
            </CCText>
          </View>

          {/* Includes */}
          <View style={seriesStyles.includes}>
            {[
              '1 tournament with opening positions',
              'Personalised review report',
              'Free Play & Learn platform',
            ].map((text, i) => (
              <View key={i} style={seriesStyles.includeRow}>
                <TickCheck />
                <CCText
                  fontSize={13}
                  fontWeight="medium"
                  color="#262322"
                  style={seriesStyles.includeText}>
                  {text}
                </CCText>
              </View>
            ))}
          </View>

          {/* CTA */}
          <View style={seriesStyles.ctaWrap}>
            <CouponCode
              basePrice={mrp}
              currentEffectivePrice={
                appliedCoupon
                  ? couponDiscountedPrice ?? undefined
                  : hasDiscount
                  ? categoryAPrice ?? undefined
                  : undefined
              }
              discountedPrice={couponDiscountedPrice}
              currentDiscountLabel={
                !appliedCoupon && hasDiscount ? 'Series Discount' : undefined
              }
              pendingDiscountedPrice={
                pendingCoupon
                  ? computeFinalPrice(pendingCoupon.coupon, mrp)
                  : null
              }
              couponExtraParams={couponExtraParams}
            />
            {!!errorMessage && (
              <CCText
                fontSize={12}
                color={CCColors.Red}
                style={seriesStyles.errorMsg}>
                {errorMessage}
              </CCText>
            )}
            <TouchableOpacity
              style={[
                seriesStyles.ctaBtn,
                (isPaymentProcessing || !categoryAPrice || isLoading) &&
                  seriesStyles.ctaBtnDisabled,
              ]}
              onPress={onPayNow}
              disabled={isPaymentProcessing || !categoryAPrice || isLoading}
              activeOpacity={0.85}>
              <CCText style={seriesStyles.ctaBtnText}>
                {isPaymentProcessing
                  ? 'Processing...'
                  : `Pay ₹${
                      couponDiscountedPrice ?? categoryAPrice ?? ''
                    } & Register`}
              </CCText>
            </TouchableOpacity>
            <TouchableOpacity
              style={seriesStyles.ctaGhost}
              onPress={() =>
                Linking.openURL('https://circlechess.com/play-to-win')
              }
              activeOpacity={0.7}>
              <CCText style={seriesStyles.ctaGhostText}>Know more</CCText>
            </TouchableOpacity>
            <View style={seriesStyles.trust}>
              <View style={seriesStyles.trustItem}>
                <MaterialCommunityIcons
                  name="lock-outline"
                  size={12}
                  color="#888280"
                />
                <CCText style={seriesStyles.trustText}>
                  Secure UPI &amp; Cards
                </CCText>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
